/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from 'react';
import UseAxios from '../../../../Api/http/http';
import PropTypes from 'prop-types';
import CommonTextField from '../../../../Components/TextField/textField';
import './addEditRole.scss';
import FormDialog from '../../../../Components/Modal/modal';
import SimpleSnackbar from '../../../../Components/Snackbar/snackbar';
import { addRolesData, editRolesData } from '../../../../Api/request/role';
import {
  ADD_EDIT_DIALOG,
  ADD_EDIT_RESPONSE_MESSAGES,
  CANCEL_TEXT,
  SAVE,
  UPDATE,
} from '../../../../Constants/strings';

const RoleForm = ({
  modalOpen,
  handleClose,
  setRows,
  isEdit,
  editId,
  editValue,
  editDescription,
  isEditMode,
}) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [autoFocus, setAutoFocus] = useState(false);
  const [roleValid, setRoleValid] = useState(false);
  const [roleError, setRoleError] = useState('');
  const [roleNameValue, setRoleNameValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [severity, setSeverity] = useState('');

  useEffect(() => {
    if (isEdit) {
      setRoleNameValue(editValue);
      setDescriptionValue(editDescription);
    }
  }, [isEdit, editValue, editDescription]);

  const handleCloseWithReset = () => {
    resetValues();
    handleClose();
  };

  const resetValues = () => {
    if (!isEdit) {
      setRoleNameValue('');
      setDescriptionValue('');
    }
    setRoleError('');
  };

  const snackbarOpen = () => {
    setOpenSnackbar(true);
    setTimeout(() => {
      setOpenSnackbar(false);
    }, 4000);
  };
  // add/edit handleResponse for different response status
  const handleResponse = (responseData) => {
    switch (
      responseData.data.status
      || responseData.data.response.data.statusCode
    ) {
    case 200:
      setRows((prevRows) => {
        if (isEdit) {
          return prevRows.map((row) => {
            if (row.id === responseData.data.roleId) {
              return {
                ...row,
                roleName: roleNameValue?.trim(),
                description: descriptionValue,
              };
            }

            return row;
          });
        } else {
          return setRows();
        }
      });
      snackbarOpen();
      setSnackbarMessage(
        isEdit
          ? ADD_EDIT_RESPONSE_MESSAGES.ROLE_UPDATED_SUCCESS
          : ADD_EDIT_RESPONSE_MESSAGES.ROLE_ADDED_SUCCESS,
      );
      setSeverity('success');
      handleCloseWithReset();
      break;
    case 400:
      setRoleError(ADD_EDIT_RESPONSE_MESSAGES.ENTER_ROLE_NAME);
      setRoleValid(true);
      break;
    case 404:
      setRoleError(ADD_EDIT_RESPONSE_MESSAGES.ROLE_NAME_TAKEN);
      setRoleValid(true);
      break;
    case 500:
      snackbarOpen();
      setSnackbarMessage(
        isEdit
          ? ADD_EDIT_RESPONSE_MESSAGES.OOPS_UPDATE_TRY_AGAIN
          : ADD_EDIT_RESPONSE_MESSAGES.OOPS_TRY_AGAIN,
      );
      setSeverity('warning');
      handleCloseWithReset();
      break;
    default:
      snackbarOpen();
      setSnackbarMessage(
        isEdit
          ? ADD_EDIT_RESPONSE_MESSAGES.UNABLE_TO_UPDATE_ROLE
          : ADD_EDIT_RESPONSE_MESSAGES.UNABLE_TO_ADD_ROLE,
      );
      setSeverity('warning');
      handleCloseWithReset();
      break;
    }
  };
  // add/edit api call
  const submitAction = async (data) => {
    // eslint-disable-next-line init-declarations
    let response;
    if (isEdit) {
      response = await UseAxios(
        editRolesData(data.id, data.roleName, data.description),
      );
    } else {
      response = await UseAxios(addRolesData(data.roleName, data.description));
    }

    return response;
  };

  const handleSubmit = async () => {
    try {
      if (roleError !== '') {
        return;
      }
      // 'submitData' is a function that prepares data for submission based on whether it's an edit or add operation
      const submitData = isEdit
        ? { id: editId, roleName: roleNameValue?.trim(), description: descriptionValue }
        : { roleName: roleNameValue?.trim(), description: descriptionValue };
      const responseData = await submitAction(submitData);
      handleResponse(responseData);
      setAutoFocus(false);
    } catch (error) {
      snackbarOpen();
      setSnackbarMessage(
        isEdit
          ? ADD_EDIT_RESPONSE_MESSAGES.OOPS_UPDATE_TRY_AGAIN
          : ADD_EDIT_RESPONSE_MESSAGES.OOPS_TRY_AGAIN,
      );
      setSeverity('warning');
      handleCloseWithReset();
      console.error('Error:', error);
    }
  };

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setRoleNameValue(newValue);
    setAutoFocus(false);

    if (newValue.length < 3 || newValue.length > 100) {
      setRoleError(`${ADD_EDIT_RESPONSE_MESSAGES.ROLE_NAME_MUST_BE}`);
      setRoleValid(true);
    } else if (/[^a-zA-Z\s]| {2,}/.test(newValue)) {
      setRoleError(`${ADD_EDIT_RESPONSE_MESSAGES.ROLE_NAME_INVALID}`);
      setRoleValid(true);
    } else {
      setRoleError('');
    }
  };

  const handleDescriptionChange = (event) => {
    const newValue = event.target.value;
    if (newValue.length <= 100) {
      setDescriptionValue(newValue);
      setAutoFocus(true);
    }
  };

  return (
    <div>
      <FormDialog // add/edit dialog common component
        title={
          isEditMode ? ADD_EDIT_DIALOG.EDIT_ROLE : ADD_EDIT_DIALOG.ADD_ROLE
        }
        open={modalOpen}
        button1Action={handleCloseWithReset}
        dialogPaperCss="add-role-dialog-paper"
        dialogCss="role-dialog"
        dialogBodycontent={
          <div className="role-dialog-body">
            <CommonTextField
              textCss="role-name"
              label={ADD_EDIT_DIALOG.ROLE_NAME}
              value={roleNameValue}
              onChange={handleInputChange}
              placeholder={ADD_EDIT_DIALOG.ROLE_NAME_PLACEHOLDER}
              autoFocus={true}
              size="small"
              id={isEdit ? 'edit-role-name-text' : 'add-role-name-text'}
            />
            {roleValid && (
              <div className={isEdit ? 'edit-role-error' : 'role-error'}>
                <small>{roleError}</small>
              </div>
            )}
            <CommonTextField
              textCss="role-description"
              label={ADD_EDIT_DIALOG.DESCRIPTION}
              value={descriptionValue}
              onChange={handleDescriptionChange}
              placeholder={ADD_EDIT_DIALOG.DESCRIPTION_PLACEHOLDER}
              autoFocus={autoFocus}
              size="small"
              maxLength={100}
              id={isEdit ? 'edit-description-text' : 'add-description-text'}
            />
          </div>
        }
        button1={CANCEL_TEXT}
        button2={isEditMode ? UPDATE : SAVE}
        button2Action={handleSubmit}
        cancelButtonId={isEdit ? 'edit-role-cancel-btn' : 'add-role-cancel-btn'}
        submitButtonId={isEdit ? 'edit-role-submit-btn' : 'add-role-submit-btn'}
      />
      <SimpleSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={severity}
        alertCss={`${isEdit ? 'edit-snackbar' : 'add-snackbar'} ${severity}`}
      />
    </div>
  );
};

RoleForm.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  setRows: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  editId: PropTypes.number,
  editValue: PropTypes.string,
  editDescription: PropTypes.string,
  isEditMode: PropTypes.bool,
};

export default RoleForm;
